import React, { useState } from 'react';
import { Video, Film, Clapperboard, Lightbulb } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Video: Video,
  Film: Film,
  Clapperboard: Clapperboard,
  Lightbulb: Lightbulb
};

const ServicesSection = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="services" className="relative bg-black py-20 lg:py-32 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 futuristic-grid opacity-20" />
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-20 bg-gradient-to-b from-red-600/0 via-red-600/50 to-red-600/0"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animation: 'float 10s ease-in-out infinite'
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 transform-3d">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 text-3d">
            What We{' '}
            <span className="holographic animate-gradient">Create</span>
          </h2>
          <div className="relative">
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">
              From concept to screen, we deliver cinematic excellence across commercials, documentaries, and brand content.
            </p>
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-red-600 to-transparent" />
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isHovered = hoveredIndex === index;
            
            return (
              <div
                key={service.id}
                className="group relative transform-3d"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 150}ms both`
                }}
              >
                <div className="flip-card h-full">
                  <div className={`flip-card-inner ${isHovered ? '' : ''}`}>
                    {/* Front Face */}
                    <div className="flip-card-front glass-card p-8 rounded-3xl border border-white/10 hover-lift transition-all duration-500 h-full min-h-[300px] relative overflow-hidden">
                      {/* Holographic Corner Accents */}
                      <div className="absolute top-0 right-0 w-20 h-20 holographic opacity-20 rounded-bl-[100%]" />
                      <div className="absolute bottom-0 left-0 w-20 h-20 holographic opacity-20 rounded-tr-[100%]" />

                      {/* Rotating Border on Hover */}
                      <div className={`absolute inset-0 rounded-3xl transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="absolute inset-0 rounded-3xl border-2 border-red-600/50 animate-spin-slow" />
                      </div>

                      {/* Icon with 3D Effect */}
                      <div className="relative mb-6 inline-block">
                        <div className="relative z-10 glass-ultra p-4 rounded-2xl neon-red transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                          <IconComponent size={40} className="text-red-600" />
                        </div>
                        <div className="absolute inset-0 bg-red-600 blur-2xl opacity-50 group-hover:opacity-75 transition-opacity animate-pulse-glow" />
                      </div>

                      {/* Content */}
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 relative z-10">
                        {service.title}
                      </h3>
                      <p className="text-white/70 leading-relaxed relative z-10">
                        {service.description}
                      </p>

                      {/* Animated Bottom Line */}
                      <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-red-600 via-white to-red-600 w-0 group-hover:w-full transition-all duration-700" />
                      
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 -left-20 w-40 h-40 bg-red-600 rounded-full blur-[100px] opacity-20 animate-pulse-glow" />
        <div className="absolute bottom-1/4 -right-20 w-40 h-40 bg-white rounded-full blur-[100px] opacity-10 animate-pulse-glow" />
      </div>
    </section>
  );
};

export default ServicesSection;
